import { Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setUser } from '@/redux/slices/authSlice';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/authService';

import AuthLayout from '@/layouts/AuthLayout';
import MainLayout from '@/layouts/MainLayout';
import ProtectedRoute from '@/components/ProtectedRoute';

import Login from '@/pages/auth/Login';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';

import EmployeeDashboard from '@/pages/dashboard/EmployeeDashboard';
import AdminDashboard from '@/pages/dashboard/AdminDashboard';

import Attendance from '@/pages/Attendance';
import Leaves from '@/pages/Leaves';
import NewLeave from '@/pages/NewLeave';
import LeaveDetail from '@/pages/LeaveDetail';
import Holidays from '@/pages/Holidays';
import Tasks from '@/pages/Tasks';
import TaskDetail from '@/pages/TaskDetail';
import Projects from '@/pages/Projects';
import NewProject from '@/pages/NewProject';
import ProjectDetail from '@/pages/ProjectDetail';
import ProjectChat from '@/pages/ProjectChat';
import WorkReports from '@/pages/WorkReports';
import Profile from '@/pages/Profile';
import Chats from '@/pages/Chats';
import ChatRoom from '@/pages/ChatRoom';
import Notifications from '@/pages/Notifications';
import DesktopApp from '@/pages/DesktopApp';

import Employees from '@/pages/admin/Employees';
import AdminLeaves from '@/pages/admin/AdminLeaves';
import AdminHolidays from '@/pages/admin/AdminHolidays';
import AdminAttendance from '@/pages/admin/AdminAttendance';
import AdminWorkReports from '@/pages/admin/AdminWorkReports';
import Reports from '@/pages/admin/Reports';
import TrackerLive from '@/pages/admin/TrackerLive';
import TrackerDay from '@/pages/admin/TrackerDay';
import TrackerScreenshots from '@/pages/admin/TrackerScreenshots';
import TrackerSettings from '@/pages/admin/TrackerSettings';

import Landing from '@/pages/Landing';
import NotFound from '@/pages/NotFound';

export default function App() {
  const { isAuthenticated } = useAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isAuthenticated) {
      authService
        .me()
        .then((r) => dispatch(setUser(r.user)))
        .catch(() => {});
    }
  }, [isAuthenticated, dispatch]);

  return (
    <Routes>
      {/* Public marketing page. It is the site's front door, so it stays outside
          both ProtectedRoute and MainLayout — a visitor with no account has to be
          able to read it, and a signed-in one gets a Dashboard button instead of
          a redirect. */}
      <Route path="/" element={<Landing />} />

      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
      </Route>

      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route path="/attendance" element={<Attendance />} />

        {/* Employee self-service. Not for admins — they have no leave balance and
            no attendance of their own. /leaves/:id stays shared below, because
            that is the screen an admin reviews a request on. */}
        <Route element={<ProtectedRoute requireEmployee />}>
          <Route path="/dashboard" element={<EmployeeDashboard />} />
        </Route>
        <Route element={<ProtectedRoute requireEmployee adminRedirect="/admin/leaves" />}>
          <Route path="/leaves" element={<Leaves />} />
          <Route path="/leaves/new" element={<NewLeave />} />
        </Route>

        <Route path="/leaves/:id" element={<LeaveDetail />} />
        <Route path="/holidays" element={<Holidays />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/tasks/:id" element={<TaskDetail />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/new" element={<NewProject />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/projects/:id/chat" element={<ProjectChat />} />
        <Route path="/work-reports" element={<WorkReports />} />
        <Route path="/chats" element={<Chats />} />
        <Route path="/chats/:id" element={<ChatRoom />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/desktop-app" element={<DesktopApp />} />

        <Route element={<ProtectedRoute requireAdmin />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/employees" element={<Employees />} />
          <Route path="/admin/attendance" element={<AdminAttendance />} />
          <Route path="/admin/leaves" element={<AdminLeaves />} />
          <Route path="/admin/holidays" element={<AdminHolidays />} />
          <Route path="/admin/work-reports" element={<AdminWorkReports />} />
          <Route path="/admin/reports" element={<Reports />} />
          <Route path="/admin/tracker" element={<TrackerLive />} />
          <Route path="/admin/tracker/settings" element={<TrackerSettings />} />
          <Route path="/admin/tracker/screenshots" element={<TrackerScreenshots />} />
          <Route path="/admin/tracker/:userId" element={<TrackerDay />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
